import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import Link from 'next/link';

import { Image } from '@/components/common/image';
import { StyledParagraph } from '@/components/common/MarkUp';
import ChevronRightIcon from '@/public/images/chevron-right.svg';
import StandoutCtaStoryIcon from '@/public/images/standout-cta-story-icon.svg';

import type { SanityImageAsset, SanityReference } from 'sanity-codegen';

export type StandoutBlock = {
  caption?: string;
  title: string;
  url: string;
  trackingTag?: {
    _type: 'trackingTag';
    blockName: string;
    blockAction: string;
  };
  variant?: 'default' | 'icon' | 'image';
  image?: {
    _type: 'image';
    asset: SanityReference<SanityImageAsset>;
    alternateText?: string;
  };
};

export interface StandoutCTAListProps {
  cards: StandoutBlock[];
  variant?: 'employers' | 'individuals';
}

export interface StandoutCTAProps {
  card: StandoutBlock;
  themeVariant?: 'employers' | 'individuals';
}

const StandoutCTAList = ({
  cards,
  variant: themeVariant,
}: StandoutCTAListProps) => {
  return (
    <>
      {cards.map((card) => (
        <StandoutCTA card={card} themeVariant={themeVariant} key={card.title} />
      ))}
    </>
  );
};

const StandoutCTA = ({ card, themeVariant }: StandoutCTAProps) => {
  const { caption, title, url, variant = 'icon', image } = card;
  const { blockName = 'standout', blockAction = 'cta' } =
    card.trackingTag ?? {};

  return (
    <CardContainer>
      <Link href={url} className={`tracking-${blockName}__${blockAction}`}>
        <CardContent>
          {(variant === 'default' || !image || !image.asset) && (
            <IconContainer>
              <DefaultIcon themeVariant={themeVariant} />
              <ChevronContainer>
                <ChevronRight />
              </ChevronContainer>
            </IconContainer>
          )}
          {variant === 'icon' && image && image.asset && (
            <IconContainer>
              <StyledImage {...image} objectFit="contain" height="56" />
              <ChevronContainer>
                <ChevronRight />
              </ChevronContainer>
            </IconContainer>
          )}
          {variant === 'image' && image && image.asset && (
            <ImageContainer>
              <StyledImage {...image} objectFit="cover" />
            </ImageContainer>
          )}
          <CaptionContainer size="caption-sm">{caption}</CaptionContainer>
          <HeadingContainer size="label-sm">{title}</HeadingContainer>
        </CardContent>
      </Link>
    </CardContainer>
  );
};

const CaptionContainer = styled(StyledParagraph)`
  font-weight: var(--font-weight-bold);
  color: var(--colour-deep-space-700);
  padding-bottom: var(--spacing-50);
`;
const HeadingContainer = styled(StyledParagraph)`
  color: var(--colour-deep-space);
  font-weight: var(--font-weight-bold);
`;
const CardContent = styled.div`
  position: relative;
  padding: var(--spacing-200);
`;
const IconContainer = styled.div`
  position: relative;
  padding-bottom: var(--spacing-100);
  > div,
  img {
    max-height: 56px;
    width: 100%;
  }
`;

const StyledImage = styled(Image)`
  width: auto !important;
  min-width: unset !important;
  margin: 0 !important;
`;

const ImageContainer = styled.div`
  position: relative;
  padding-bottom: var(--spacing-100);
  top: calc(0px - var(--spacing-200));
  left: calc(0px - var(--spacing-200));
  width: calc(100% + var(--spacing-400));
  height: 112px;
  ${StyledImage} {
    width: 100% !important;
    border-radius: 8px 8px 0 0;
    max-height: 112px;
    width: 100%;
  }
`;

const ChevronContainer = styled.div`
  height: 100%;
  position: absolute;
  top: 0;
`;

const ChevronRight = styled(ChevronRightIcon)`
  height: 15px;
  position: absolute;
  top: calc(50% - (15px / 2));
  right: 5px;
`;
const DefaultIcon = styled(StandoutCtaStoryIcon, {
  shouldForwardProp: isPropValid,
})<{
  themeVariant?: 'employers' | 'individuals';
}>`
  > path {
    ${({ themeVariant }) =>
      themeVariant === 'individuals' && `fill:var(--colour-aurora);`}
    ${({ themeVariant }) =>
      themeVariant !== 'individuals' && `fill:var(--colour-electron);`}
  }
`;

const CardContainer = styled.div`
  border: 1px solid var(--colour-border-subtle);
  border-radius: 8px;
  transition: all 0.3s;
  a {
    text-decoration: none;
  }
  &:not(:first-of-type) {
    margin-top: var(--spacing-200);
  }

  &:hover {
    transform: scale(1.01);
    p:last-of-type {
      text-decoration: underline;
    }
  }
`;

export { StandoutCTAList };
